/**
 * Branded U.S. hybrid cookie banner backed by Shopify Customer Privacy API.
 *
 * Counsel-review prototype on unpublished theme only.
 * Does not record a new consent choice merely because a page loaded.
 * Accept never enables Targeted Advertising (sale_of_data).
 *
 * Persistence: first-party host-only cookie `hs_consent` (localStorage mirror).
 * Preview / Theme Editor / shopifypreview.com origins cannot share that cookie
 * with production hostnames. Do not weaken production persistence to hide
 * repeated preview prompts — that is expected, not a production defect.
 */
(function () {
  'use strict';

  var PRIOR_CONSENT_COUNTRIES = {
    AT: 1, BE: 1, BG: 1, HR: 1, CY: 1, CZ: 1, DK: 1, EE: 1, FI: 1, FR: 1, DE: 1,
    GR: 1, HU: 1, IE: 1, IT: 1, LV: 1, LT: 1, LU: 1, MT: 1, NL: 1, PL: 1, PT: 1,
    RO: 1, SK: 1, SI: 1, ES: 1, SE: 1, GB: 1, UK: 1, IS: 1, LI: 1, NO: 1, CH: 1
  };

  /* Retention is 6 months. Change CONSENT_RETENTION_DAYS to update duration without rewriting logic. */
  var CONSENT_COOKIE = 'hs_consent';
  var CONSENT_VERSION = 1;
  var CONSENT_RETENTION_DAYS = 180;
  var CONSENT_MAX_AGE = CONSENT_RETENTION_DAYS * 24 * 60 * 60;

  var banner = document.querySelector('[data-hs-cookie-banner]');
  var prefs = document.querySelector('[data-hs-cookie-prefs]');
  if (!banner) return;

  var customerPrivacy = null;
  var lastFocus = null;
  var storedAtLoad = readStoredConsent({ allowExpired: true });
  if (storedAtLoad && !storedAtLoad.expired) hideBanner();

  function hasGpc() {
    return Boolean(navigator.globalPrivacyControl);
  }

  function visitorCountry() {
    var fromBanner = banner.getAttribute('data-hs-country');
    var fromShopify = window.Shopify && window.Shopify.country;
    return String(fromBanner || fromShopify || 'US').toUpperCase();
  }

  function isPriorConsentRegion() {
    return Boolean(PRIOR_CONSENT_COUNTRIES[visitorCountry()]);
  }

  function readCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length < 2) return '';
    return decodeURIComponent(parts.pop().split(';').shift() || '');
  }

  function writeCookie(name, value, maxAge) {
    var parts = [
      name + '=' + encodeURIComponent(value),
      'Path=/',
      'Max-Age=' + String(maxAge),
      'SameSite=Lax'
    ];
    if (location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
    try {
      if (maxAge <= 0) localStorage.removeItem(name);
      else localStorage.setItem(name, value);
    } catch (error) { /* private mode */ }
  }

  function rawStoredPayload() {
    var raw = readCookie(CONSENT_COOKIE);
    if (!raw) {
      try { raw = localStorage.getItem(CONSENT_COOKIE) || ''; } catch (error) { raw = ''; }
    }
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function readStoredConsent(opts) {
    var allowExpired = opts && opts.allowExpired;
    var data = rawStoredPayload();
    if (!data || typeof data !== 'object') return null;
    if (data.v !== CONSENT_VERSION) return null;
    if (typeof data.ts !== 'number' || !data.choice) return null;
    var age = Date.now() - data.ts;
    var ttl = CONSENT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    if (age < 0 || age > ttl) {
      return allowExpired ? { expired: true, v: data.v, choice: data.choice, ts: data.ts } : null;
    }
    return {
      v: data.v,
      choice: data.choice,
      analytics: Boolean(data.analytics),
      marketing: Boolean(data.marketing),
      preferences: Boolean(data.preferences),
      sale_of_data: Boolean(data.sale_of_data),
      ts: data.ts,
      expired: false
    };
  }

  function inferChoice(payload) {
    if (payload.analytics && payload.marketing && payload.preferences && !payload.sale_of_data) return 'accept';
    if (!payload.analytics && !payload.marketing && !payload.preferences && !payload.sale_of_data) return 'decline';
    return 'custom';
  }

  function persistConsent(choice, payload) {
    var data = {
      v: CONSENT_VERSION,
      choice: choice || inferChoice(payload),
      analytics: Boolean(payload.analytics),
      marketing: Boolean(payload.marketing),
      preferences: Boolean(payload.preferences),
      sale_of_data: Boolean(payload.sale_of_data),
      ts: Date.now()
    };
    writeCookie(CONSENT_COOKIE, JSON.stringify(data), CONSENT_MAX_AGE);
    return data;
  }

  function currentConsent() {
    try {
      return (customerPrivacy && customerPrivacy.currentVisitorConsent()) || {};
    } catch (error) {
      return {};
    }
  }

  function hasShopifyChoice() {
    var consent = currentConsent();
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function (key) {
      return consent[key] === 'yes' || consent[key] === 'no';
    });
  }

  function hasRecordedChoice() {
    var stored = readStoredConsent({ allowExpired: true });
    if (stored && !stored.expired) return true;
    if (stored && stored.expired) return false;
    return hasShopifyChoice();
  }

  function effectiveConsent() {
    var stored = readStoredConsent();
    if (stored) {
      return {
        analytics: stored.analytics ? 'yes' : 'no',
        marketing: stored.marketing ? 'yes' : 'no',
        preferences: stored.preferences ? 'yes' : 'no',
        sale_of_data: stored.sale_of_data ? 'yes' : 'no'
      };
    }
    return currentConsent();
  }

  function constrainConsent(next) {
    var out = {
      analytics: Boolean(next.analytics),
      marketing: Boolean(next.marketing),
      preferences: Boolean(next.preferences),
      sale_of_data: Boolean(next.sale_of_data)
    };
    if (hasGpc()) out.sale_of_data = false;
    return out;
  }

  function setConsent(next, done, choice) {
    var payload = constrainConsent(next);
    persistConsent(choice || inferChoice(payload), payload);
    if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') {
      if (done) done(new Error('privacy api unavailable'));
      return;
    }
    customerPrivacy.setTrackingConsent(payload, function () {
      document.dispatchEvent(new CustomEvent('hs:consent-updated', { detail: payload }));
      if (done) done();
    });
  }

  function acceptConsent() {
    return {
      analytics: true,
      marketing: true,
      preferences: true,
      sale_of_data: false
    };
  }

  function declineConsent() {
    return {
      analytics: false,
      marketing: false,
      preferences: false,
      sale_of_data: false
    };
  }

  function showBanner() {
    banner.hidden = false;
  }

  function hideBanner() {
    banner.hidden = true;
  }

  function shopifyPayloadFromConsent(consent) {
    return {
      analytics: consent.analytics === 'yes',
      marketing: consent.marketing === 'yes',
      preferences: consent.preferences === 'yes',
      sale_of_data: consent.sale_of_data === 'yes'
    };
  }

  function adoptShopifyConsentIfNeeded() {
    var stored = readStoredConsent({ allowExpired: true });
    if (stored) return;
    if (!hasShopifyChoice()) return;
    var payload = constrainConsent(shopifyPayloadFromConsent(currentConsent()));
    persistConsent(inferChoice(payload), payload);
  }

  function restoreToShopify() {
    var stored = readStoredConsent();
    if (!stored || !customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') return;
    var current = currentConsent();
    var matches =
      current.analytics === (stored.analytics ? 'yes' : 'no') &&
      current.marketing === (stored.marketing ? 'yes' : 'no') &&
      current.preferences === (stored.preferences ? 'yes' : 'no') &&
      current.sale_of_data === (stored.sale_of_data ? 'yes' : 'no');
    if (matches) return;
    /* Re-apply a previously recorded choice so Shopify pixels match the first-party record. Not a new default. */
    customerPrivacy.setTrackingConsent(constrainConsent(stored), function () {
      document.dispatchEvent(new CustomEvent('hs:consent-updated', { detail: constrainConsent(stored) }));
    });
  }

  function syncPreferenceFields() {
    var consent = effectiveConsent();
    ['analytics', 'marketing', 'preferences', 'sale_of_data'].forEach(function (key) {
      var input = prefs && prefs.querySelector('[name="' + key + '"]');
      if (!input || input.disabled && key !== 'sale_of_data') return;
      input.checked = consent[key] === 'yes';
    });
  }

  function applyPreferenceDefaults() {
    if (!prefs) return;
    var prior = isPriorConsentRegion();
    var saleInput = prefs.querySelector('[name="sale_of_data"]');
    var prefsInput = prefs.querySelector('[name="preferences"]');
    var analyticsInput = prefs.querySelector('[name="analytics"]');
    var marketingInput = prefs.querySelector('[name="marketing"]');

    if (hasRecordedChoice()) {
      syncPreferenceFields();
    } else {
      if (prefsInput) prefsInput.checked = !prior;
      if (analyticsInput) analyticsInput.checked = !prior;
      if (marketingInput) marketingInput.checked = false;
      if (saleInput) saleInput.checked = false;
    }

    if (saleInput) {
      if (hasGpc()) {
        saleInput.checked = false;
        saleInput.disabled = true;
      } else {
        saleInput.disabled = false;
      }
    }

    var note = prefs.querySelector('[data-hs-gpc-note]');
    if (note) note.hidden = !hasGpc();
  }

  function openPrefs() {
    if (!prefs) return;
    lastFocus = document.activeElement;
    applyPreferenceDefaults();
    prefs.hidden = false;
    var first = prefs.querySelector('input:not([disabled]), button');
    if (first) first.focus();
  }

  function closePrefs() {
    if (!prefs) return;
    prefs.hidden = true;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  function shouldDisplayBanner() {
    var stored = readStoredConsent({ allowExpired: true });
    if (stored && !stored.expired) return false;
    if (stored && stored.expired) return true;
    if (hasShopifyChoice()) return false;
    return true;
  }

  function bind() {
    if (banner.getAttribute('data-hs-cookie-bound')) return;
    banner.setAttribute('data-hs-cookie-bound', 'true');
    banner.addEventListener('click', function (event) {
      var action = event.target.getAttribute && event.target.getAttribute('data-hs-cookie-action');
      if (!action) return;
      event.preventDefault();
      if (action === 'accept') {
        setConsent(acceptConsent(), function () {
          hideBanner();
          closePrefs();
        }, 'accept');
      } else if (action === 'decline') {
        setConsent(declineConsent(), function () {
          hideBanner();
          closePrefs();
        }, 'decline');
      } else if (action === 'preferences') {
        openPrefs();
      }
    });

    if (prefs) {
      prefs.addEventListener('click', function (event) {
        if (event.target.hasAttribute('data-hs-cookie-prefs-dismiss')) {
          event.preventDefault();
          closePrefs();
        }
      });
      var form = prefs.querySelector('form');
      if (form) {
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          setConsent({
            analytics: Boolean(form.analytics && form.analytics.checked),
            marketing: Boolean(form.marketing && form.marketing.checked),
            preferences: Boolean(form.preferences && form.preferences.checked),
            sale_of_data: Boolean(form.sale_of_data && form.sale_of_data.checked && !form.sale_of_data.disabled)
          }, function () {
            hideBanner();
            closePrefs();
          }, 'custom');
        });
      }
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !prefs.hidden) closePrefs();
      });
    }

    document.addEventListener('click', function (event) {
      var control = event.target.closest && event.target.closest('[data-cookie-preferences]');
      if (!control) return;
      event.preventDefault();
      openPrefs();
    }, true);
  }

  function revealFooterControls() {
    document.querySelectorAll('[data-cookie-preferences]').forEach(function (control) {
      control.hidden = false;
      control.style.display = '';
    });
  }

  function applyBannerVisibility() {
    if (shouldDisplayBanner()) showBanner();
    else hideBanner();
  }

  function ready(api) {
    customerPrivacy = api;
    bind();
    adoptShopifyConsentIfNeeded();
    restoreToShopify();
    applyBannerVisibility();
    revealFooterControls();
  }

  function loadApi() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      bind();
      applyBannerVisibility();
      revealFooterControls();
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error || !window.Shopify.customerPrivacy) {
        bind();
        applyBannerVisibility();
        revealFooterControls();
        return;
      }
      ready(window.Shopify.customerPrivacy);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadApi, { once: true });
  } else {
    loadApi();
  }
})();
